import * as turf from '@turf/turf'

export const DRAFT_VERSION = 1
export const DEFAULT_COLUMNS = [{ key: 'Nom', type: 'text' }]
const RESERVED = new Set(['id', 'layer_id', 'source', 'geometry'])
const GEOMETRY_LABELS = {
  Point: 'Point',
  LineString: 'Ligne',
  Polygon: 'Polygone',
}

export const parseNumberInput = raw => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { value: raw } : { error: 'Nombre invalide' }
  }
  const text = String(raw ?? '')
    .replace(/\s/g, '')
    .replace(',', '.')
  if (text === '') return { value: '' }
  const n = Number(text)
  return Number.isFinite(n) ? { value: n } : { error: 'Nombre invalide' }
}

export const validateColumnName = (name, columns, currentKey = null) => {
  const key = String(name ?? '').trim()
  const lower = key.toLowerCase()
  if (!key) return 'Nom requis'
  if (key.length > 63) return 'Nom trop long'
  if (RESERVED.has(lower) || lower.startsWith('elda_') || key.startsWith('_')) {
    return 'Nom réservé'
  }
  if (
    columns.some(c => c.key !== currentKey && c.key.toLowerCase() === lower)
  ) {
    return 'Cette colonne existe déjà'
  }
  return null
}

export const renameProperty = (features, from, to) =>
  features.map(f => {
    const properties = { ...(f.properties || {}) }
    const value = properties[from]
    delete properties[from]
    return { ...f, properties: { ...properties, [to]: value ?? '' } }
  })

export const removeProperty = (features, key) =>
  features.map(f => {
    const properties = { ...(f.properties || {}) }
    delete properties[key]
    return { ...f, properties }
  })

export const firstTextColumn = columns =>
  columns.find(c => c.type === 'text')?.key ?? null

export const featureTitle = (feature, columns, index) => {
  const key = firstTextColumn(columns)
  const value = key ? String(feature.properties?.[key] ?? '').trim() : ''
  return (
    value ||
    `${GEOMETRY_LABELS[feature.geometry?.type] ?? 'Entité'} ${index + 1}`
  )
}

const cleanValue = (column, raw) => {
  if (column.type === 'boolean') return raw === true
  return column.type === 'number'
    ? parseNumberInput(raw).value ?? ''
    : String(raw ?? '').trim()
}

export const invalidValueCount = (features, columns) =>
  features.reduce(
    (n, f) =>
      n +
      columns.filter(
        c =>
          c.type === 'number' && parseNumberInput(f.properties?.[c.key]).error,
      ).length,
    0,
  )

export const buildLayerFeatures = (features, columns) => ({
  features: features.map(f => ({
    type: 'Feature',
    geometry: f.geometry,
    properties: Object.fromEntries(
      columns.map(c => [c.key, cleanValue(c, f.properties?.[c.key])]),
    ),
  })),
  propertiesOrder: columns.map(c => c.key),
  labelProperty: firstTextColumn(columns),
})

export const pillAnchor = feature => {
  const { type, coordinates } = feature?.geometry || {}
  if (type === 'Point') return coordinates
  if (type === 'LineString' && coordinates?.length >= 2) {
    const line = turf.lineString(coordinates)
    return turf.along(line, turf.length(line) / 2).geometry.coordinates
  }
  if (type === 'Polygon' && coordinates?.[0]?.length >= 4) {
    return turf.pointOnFeature(feature).geometry.coordinates
  }
  return null
}

export const draftKey = (userId, resortId) =>
  `elda.layerDraft.v${DRAFT_VERSION}:${userId}:${resortId}`

const forget = key => {
  try {
    localStorage.removeItem(key)
  } catch {
    /* stockage indisponible */
  }
}

export const readDraft = key => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const draft = JSON.parse(raw)
    if (
      draft?.version === DRAFT_VERSION &&
      Array.isArray(draft.features) &&
      Array.isArray(draft.columns)
    ) {
      return draft
    }
  } catch {
    /* illisible : traité comme absent */
  }
  forget(key)
  return null
}

export const writeDraft = (key, { meta, columns, features }) => {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        version: DRAFT_VERSION,
        savedAt: new Date().toISOString(),
        meta,
        columns,
        features,
      }),
    )
  } catch {
    /* quota plein ou navigation privée : on perd seulement le filet de sécurité */
  }
}

export const clearDraft = forget
